import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { AuthResponse, LoginData, RegisterData, User } from '../../models/user.model';
import { API_BASE_URL, AUTH_TOKEN_KEY, AUTH_USER_KEY } from '../../config/api.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authBaseUrl = API_BASE_URL;
  private authSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
  private userSubject = new BehaviorSubject<User | null>(this.getStoredUser());

  auth$ = this.authSubject.asObservable();
  user$ = this.userSubject.asObservable();

  private http = inject(HttpClient);

  login(data: LoginData): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.authBaseUrl}/auth/local`, data)
      .pipe(tap(response => this.handleAuthResponse(response)));
  }

  register(data: RegisterData): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.authBaseUrl}/auth/local/register`, data)
      .pipe(tap(response => this.handleAuthResponse(response)));
  }

  logout(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    this.authSubject.next(false);
    this.userSubject.next(null);
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.authBaseUrl}/auth/forgot-password`, { email });
  }

  resetPassword(data: { code: string; password: string; passwordConfirmation: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.authBaseUrl}/auth/reset-password`, data);
  }

  changePassword(data: {
    currentPassword: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.authBaseUrl}/auth/change-password`, data);
  }

  requestEmailConfirmation(email: string): Observable<void> {
    return this.http.post<void>(`${this.authBaseUrl}/auth/send-email-confirmation`, { email });
  }

  getToken(): string | null {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return null;
    }

    if (this.isTokenExpired(token)) {
      this.logout();
      return null;
    }

    return token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Retourne l'utilisateur courant (snapshot synchrone).
   * Utiliser `user$` pour souscrire aux changements.
   */
  getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  /**
   * Type Strapi du rôle de l'utilisateur courant (ex: `authenticated`, `public`, `admin`).
   * `type` est la clé canonique côté Strapi (indépendante de la langue),
   * c'est elle qu'on doit comparer dans les guards.
   */
  getCurrentUserRoleType(): string | null {
    return this.userSubject.value?.role?.type ?? null;
  }

  private handleAuthResponse(response: AuthResponse): void {
    localStorage.setItem(AUTH_TOKEN_KEY, response.jwt);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
    this.authSubject.next(true);
    this.userSubject.next(response.user);
  }

  private getStoredUser(): User | null {
    const user = localStorage.getItem(AUTH_USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return true;
      }

      const payload = JSON.parse(this.base64UrlDecode(payloadPart)) as { exp?: number };
      if (typeof payload.exp !== 'number') {
        return true;
      }

      const nowInSeconds = Math.floor(Date.now() / 1000);
      return payload.exp <= nowInSeconds;
    } catch {
      return true;
    }
  }

  private base64UrlDecode(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    return atob(`${base64}${padding}`);
  }
}
